<template>
  <div class="runestone-view">
    <h1>{{ texts.title }}</h1>
    <p class="lead">Hávamál — <em>«Fä dör, fränder dör…»</em> ristad i yngre futhark på ormslingan.</p>

    <div class="stones">
      <!-- ===================== STEN 1: Om Mig (röd slinga) ===================== -->
      <figure class="stone-figure">
        <svg viewBox="0 0 480 660" class="runestone-svg" role="img" aria-label="Runsten med ormslinga och texten Om Mig">
          <defs>
            <radialGradient id="granite1" cx="42%" cy="32%" r="85%">
              <stop offset="0%" stop-color="#b9b6ac" />
              <stop offset="55%" stop-color="#9a978c" />
              <stop offset="100%" stop-color="#6f6c62" />
            </radialGradient>
            <filter id="rough1" x="-10%" y="-10%" width="120%" height="120%">
              <feTurbulence type="fractalNoise" baseFrequency="0.014 0.02" numOctaves="3" seed="7" result="n" />
              <feDisplacementMap in="SourceGraphic" in2="n" scale="9" />
            </filter>
            <filter id="grain1">
              <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="2" seed="3" result="n" />
              <feColorMatrix in="n" type="matrix" values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 0.5 0" result="a" />
              <feComposite in="a" in2="SourceGraphic" operator="in" />
            </filter>
            <path id="band1" d="M228,590 C150,592 96,584 94,512 L90,150 C88,104 150,92 240,92 C330,92 392,104 390,150 L394,512 C395,580 330,588 262,588" />
          </defs>

          <g filter="url(#rough1)">
            <path d="M74,34 C50,40 40,78 44,120 L52,566 C55,614 96,632 146,634 L356,636 C408,634 440,602 436,556 L426,116 C423,66 396,34 352,32 Z"
                  fill="url(#granite1)" stroke="#57544b" stroke-width="4" />
            <path d="M74,34 C50,40 40,78 44,120 L52,566 C55,614 96,632 146,634 L356,636 C408,634 440,602 436,556 L426,116 C423,66 396,34 352,32 Z"
                  fill="#000" opacity="0.10" filter="url(#grain1)" />
          </g>

          <use href="#band1" fill="none" stroke="#1a1613" stroke-width="34" stroke-linecap="round" />
          <use href="#band1" fill="none" stroke="#7c2a1c" stroke-width="26" stroke-linecap="round" />
          <text class="runes" font-size="17" fill="#120d0a" letter-spacing="1">
            <textPath href="#band1" startOffset="2%">{{ havamalRunes }}</textPath>
          </text>

          <g transform="translate(228,590) rotate(-118) scale(1.12)">
            <path d="M2,-9 C-12,-17 -9,-33 -22,-35 C-15,-24 -15,-13 -2,-7 Z" fill="#7c2a1c" stroke="#1a1613" stroke-width="2.2" stroke-linejoin="round" />
            <path d="M0,0 C-7,-15 16,-24 30,-15 C40,-9 42,-1 50,-2 C58,-3 62,3 55,8 C62,13 57,20 49,18 C41,17 40,25 30,20 C16,26 -7,15 0,0 Z" fill="#7c2a1c" stroke="#1a1613" stroke-width="2.4" stroke-linejoin="round" />
            <path d="M49,6 C57,9 64,8 72,13 C62,15 55,12 49,9 Z" fill="#7c2a1c" stroke="#1a1613" stroke-width="2.2" stroke-linejoin="round" />
            <circle cx="14" cy="-2" r="3.6" fill="#120d0a" />
          </g>
          <g transform="translate(262,588) rotate(158) scale(1.0)">
            <path d="M0,0 C16,-6 30,-1 42,-13 C48,-2 40,10 24,12 C14,13 6,10 4,8 Z" fill="#7c2a1c" stroke="#1a1613" stroke-width="2.4" stroke-linejoin="round" />
          </g>

          <foreignObject x="120" y="150" width="240" height="410">
            <div xmlns="http://www.w3.org/1999/xhtml" class="stone-inner">
              <div class="inner-title">{{ texts.subtitle }}</div>
              <p>{{ texts.mainText }}</p>
            </div>
          </foreignObject>
        </svg>
        <figcaption>Personsten — ormslingan bär Hávamál, mittfältet berättar om mig.</figcaption>
      </figure>

      <!-- ============ STEN 2: naturlig sten, drakhuvuden, kors, spiralorm ============ -->
      <figure class="stone-figure wide">
        <svg viewBox="0 0 520 730" class="runestone-svg" role="img" aria-label="Runsten i uppländsk stil med drakhuvuden, kors och en spiralande orm i runor, svart och rött">
          <defs>
            <radialGradient id="granite2" cx="46%" cy="26%" r="95%">
              <stop offset="0%" stop-color="#5a564d" />
              <stop offset="55%" stop-color="#3c3931" />
              <stop offset="100%" stop-color="#211e19" />
            </radialGradient>
            <filter id="rough2" x="-12%" y="-12%" width="124%" height="124%">
              <feTurbulence type="fractalNoise" baseFrequency="0.013 0.019" numOctaves="4" seed="23" result="n" />
              <feDisplacementMap in="SourceGraphic" in2="n" scale="13" />
            </filter>
            <filter id="grain2">
              <feTurbulence type="fractalNoise" baseFrequency="0.8" numOctaves="2" seed="11" result="n" />
              <feColorMatrix in="n" type="matrix" values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 0.7 0" result="a" />
              <feComposite in="a" in2="SourceGraphic" operator="in" />
            </filter>
            <!-- Rundslinga längs kanten, öppen upptill för de två drakhuvudena -->
            <path id="band2" d="M232,150 C150,150 92,176 90,258 L96,566 C99,632 176,662 260,662 C344,662 421,632 424,566 L430,258 C428,176 370,150 288,150" />
            <path id="spiral2" :d="spiral.path" />
          </defs>

          <!-- Naturlig, oregelbunden sten -->
          <g filter="url(#rough2)">
            <path d="M150,44 C96,60 74,116 82,168 L70,566 C66,632 118,672 190,678 L360,690 C430,690 452,632 446,566 L430,150 C424,86 372,40 300,36 Z"
                  fill="url(#granite2)" stroke="#191712" stroke-width="5" />
            <path d="M150,44 C96,60 74,116 82,168 L70,566 C66,632 118,672 190,678 L360,690 C430,690 452,632 446,566 L430,150 C424,86 372,40 300,36 Z"
                  fill="#000" opacity="0.18" filter="url(#grain2)" />
          </g>

          <!-- Kantslinga: svart kontur + röd fyllning + runor -->
          <use href="#band2" fill="none" stroke="#14100e" stroke-width="30" stroke-linecap="round" />
          <use href="#band2" fill="none" stroke="#8f2c1c" stroke-width="22" stroke-linecap="round" />
          <text class="runes" font-size="15" fill="#2a0f08" letter-spacing="0.8">
            <textPath href="#band2" startOffset="1%">{{ packedRunes }}</textPath>
          </text>

          <!-- Två drakhuvuden som reser sig ur kantslingan och krullar utåt -->
          <g transform="translate(232,152) rotate(-124) scale(1.05)">
            <path d="M2,-10 C-13,-18 -10,-35 -24,-37 C-16,-25 -16,-14 -2,-8 Z" fill="#8f2c1c" stroke="#14100e" stroke-width="2.4" stroke-linejoin="round" />
            <path d="M0,0 C-8,-16 17,-26 32,-16 C43,-9 45,-1 54,-2 C62,-3 67,3 60,9 C67,15 62,22 54,20 C45,18 44,26 32,21 C17,28 -8,16 0,0 Z" fill="#8f2c1c" stroke="#14100e" stroke-width="2.6" stroke-linejoin="round" />
            <path d="M54,6 C62,9 69,8 77,14 C67,16 60,13 54,10 Z" fill="#8f2c1c" stroke="#14100e" stroke-width="2.4" stroke-linejoin="round" />
            <circle cx="15" cy="-2" r="3.8" fill="#140d0a" />
          </g>
          <g transform="translate(288,152) rotate(-56) scale(1.05)">
            <path d="M2,-10 C-13,-18 -10,-35 -24,-37 C-16,-25 -16,-14 -2,-8 Z" fill="#8f2c1c" stroke="#14100e" stroke-width="2.4" stroke-linejoin="round" />
            <path d="M0,0 C-8,-16 17,-26 32,-16 C43,-9 45,-1 54,-2 C62,-3 67,3 60,9 C67,15 62,22 54,20 C45,18 44,26 32,21 C17,28 -8,16 0,0 Z" fill="#8f2c1c" stroke="#14100e" stroke-width="2.6" stroke-linejoin="round" />
            <path d="M54,6 C62,9 69,8 77,14 C67,16 60,13 54,10 Z" fill="#8f2c1c" stroke="#14100e" stroke-width="2.4" stroke-linejoin="round" />
            <circle cx="15" cy="-2" r="3.8" fill="#140d0a" />
          </g>

          <!-- Kors upptill (ritas över allt så det syns rent) -->
          <path d="M260,86 L260,206 M204,140 L316,140" stroke="#14100e" stroke-width="22" stroke-linecap="butt" />
          <path d="M260,88 L260,204 M206,140 L314,140" stroke="#9a3324" stroke-width="14" stroke-linecap="butt" />
          <path d="M260,92 L260,200 M210,140 L310,140" stroke="#c23a24" stroke-width="5" stroke-linecap="butt" />

          <!-- Inre spiralorm: svart kropp + röd kontur + röda runor -->
          <use href="#spiral2" fill="none" stroke="#8f2c1c" stroke-width="26" stroke-linecap="round" />
          <use href="#spiral2" fill="none" stroke="#14100e" stroke-width="20" stroke-linecap="round" />
          <text class="runes" font-size="13.5" fill="#c23a24" letter-spacing="0.4">
            <textPath href="#spiral2" startOffset="0.5%">{{ packedRunes }}</textPath>
          </text>

          <!-- Spiralormens svans (yttre änden) -->
          <g :transform="`translate(${spiral.tail.x},${spiral.tail.y}) rotate(${spiral.tail.rot}) scale(1.05)`">
            <path d="M0,0 C-16,-6 -30,-1 -42,-13 C-48,-2 -40,10 -24,12 C-14,13 -6,10 -4,8 Z" fill="#14100e" stroke="#8f2c1c" stroke-width="2.4" stroke-linejoin="round" />
          </g>
          <!-- Spiralormens huvud (i mitten) -->
          <g :transform="`translate(${spiral.head.x},${spiral.head.y}) rotate(${spiral.head.rot}) scale(1.05)`">
            <path d="M2,-9 C-12,-17 -9,-33 -22,-35 C-15,-24 -15,-13 -2,-7 Z" fill="#14100e" stroke="#8f2c1c" stroke-width="2.2" stroke-linejoin="round" />
            <path d="M0,0 C-7,-15 16,-24 30,-15 C40,-9 42,-1 50,-2 C58,-3 62,3 55,8 C62,13 57,20 49,18 C41,17 40,25 30,20 C16,26 -7,15 0,0 Z" fill="#14100e" stroke="#8f2c1c" stroke-width="2.4" stroke-linejoin="round" />
            <path d="M49,6 C57,9 64,8 72,13 C62,15 55,12 49,9 Z" fill="#14100e" stroke="#8f2c1c" stroke-width="2.2" stroke-linejoin="round" />
            <circle cx="14" cy="-2" r="3.4" fill="#c23a24" />
          </g>
        </svg>
        <figcaption>Ormsten — uppländsk stil: kantslinga med drakhuvuden, kors och en spiralande orm, allt i runor, målad i svart och rött.</figcaption>
      </figure>
    </div>
  </div>
</template>

<script>
import texts from '@/data/runestoneText.json';

const RUNE_MAP = {
  a: 'ᛅ', á: 'ᛅ', à: 'ᛅ', ä: 'ᛅ', æ: 'ᛅ', å: 'ᚬ',
  b: 'ᛒ', c: 'ᚴ', d: 'ᛏ',
  e: 'ᛁ', é: 'ᛁ', f: 'ᚠ', g: 'ᚴ', h: 'ᚼ',
  i: 'ᛁ', í: 'ᛁ', j: 'ᛁ', k: 'ᚴ', l: 'ᛚ', m: 'ᛘ', n: 'ᚾ',
  o: 'ᚢ', ó: 'ᚢ', ø: 'ᚢ', ö: 'ᚢ', p: 'ᛒ', q: 'ᚴ', r: 'ᚱ',
  s: 'ᛋ', t: 'ᛏ', u: 'ᚢ', ú: 'ᚢ', ü: 'ᚢ', y: 'ᚢ',
  v: 'ᚠ', w: 'ᚠ', x: 'ᚴᛋ', z: 'ᛋ', þ: 'ᚦ', ð: 'ᚦ',
};

function toRunes(text) {
  if (!text) return '';
  return text
    .toLowerCase()
    .replace(/th/g, 'þ')
    .replace(/[.,;:!?'"()]/g, ' ')
    .split(/\s+/)
    .map((word) => {
      let out = '';
      for (const ch of word) out += RUNE_MAP[ch] || '';
      return out.replace(/ᚱ$/, 'ᛦ');
    })
    .filter(Boolean)
    .join('·');
}

export default {
  name: 'RunestoneView',
  data() {
    return { texts };
  },
  computed: {
    havamalRunes() {
      const once = toRunes(this.texts.havamalSource);
      return (once + '·᛭·' + once + '·᛭·').repeat(2);
    },
    packedRunes() {
      const s = toRunes(this.texts.mainText) + '·᛭·' + toRunes(this.texts.havamalSource) + '·᛭·';
      return s.repeat(4);
    },
    // Arkimedisk spiral: ormkroppen ringlar inåt till huvudet i mitten.
    spiral() {
      const cx = 260, cy = 420, turns = 3.15, rStart = 158, rEnd = 22, steps = 260;
      const total = turns * 2 * Math.PI;
      const pts = [];
      for (let i = 0; i <= steps; i++) {
        const t = i / steps;
        const ang = t * total + Math.PI / 2;
        const r = rStart + (rEnd - rStart) * t;
        pts.push([cx + r * Math.cos(ang), cy + r * Math.sin(ang)]);
      }
      const path = pts.map((p, i) => (i ? 'L' : 'M') + p[0].toFixed(1) + ',' + p[1].toFixed(1)).join(' ');
      const deg = (a, b) => (Math.atan2(b[1] - a[1], b[0] - a[0]) * 180) / Math.PI;
      return {
        path,
        tail: { x: pts[0][0].toFixed(1), y: pts[0][1].toFixed(1), rot: deg(pts[1], pts[0]).toFixed(1) },
        head: { x: pts[steps][0].toFixed(1), y: pts[steps][1].toFixed(1), rot: deg(pts[steps - 1], pts[steps]).toFixed(1) },
      };
    },
  },
};
</script>

<style scoped>
.runestone-view {
  min-height: calc(100vh - var(--navbar-height, 70px));
  padding: 2.5rem 1rem 4rem;
  text-align: center;
  color: #e9e4d8;
  background:
    radial-gradient(1200px 500px at 50% -5%, rgba(140, 60, 30, 0.16), transparent 60%),
    linear-gradient(180deg, #241f19 0%, #17130f 60%, #100c09 100%);
}

h1 {
  font-family: 'Georgia', serif;
  font-size: clamp(2rem, 4vw, 2.9rem);
  letter-spacing: 0.06em;
  margin: 0 0 0.4rem;
  color: #f0e9da;
  text-shadow: 0 2px 12px rgba(0, 0, 0, 0.6);
}

.lead {
  color: #b9ac95;
  margin: 0 auto 2.4rem;
  max-width: 42rem;
  font-family: 'Georgia', serif;
  font-size: 1.05rem;
}
.lead em { color: #d98a5f; }

.stones {
  display: flex;
  flex-wrap: wrap;
  gap: 3rem;
  justify-content: center;
  align-items: flex-start;
  max-width: 1200px;
  margin: 0 auto;
}

.stone-figure {
  margin: 0;
  flex: 1 1 460px;
  max-width: 540px;
}
.stone-figure.wide { max-width: 600px; }

.runestone-svg {
  width: 100%;
  height: auto;
  filter: drop-shadow(0 22px 40px rgba(0, 0, 0, 0.6));
}

.runes {
  font-family: 'Noto Sans Runic', 'Segoe UI Historic', 'Georgia', serif;
  font-weight: 600;
}

figcaption {
  margin-top: 1.1rem;
  color: #a89a83;
  font-family: 'Georgia', serif;
  font-size: 0.98rem;
  line-height: 1.45;
}

.stone-inner {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  color: #211a13;
  font-family: 'Georgia', serif;
  overflow: hidden;
}
.inner-title {
  font-size: 20px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  margin: 4px 0 8px;
  color: #3a2a18;
}
.stone-inner p {
  margin: 0;
  font-size: 11.2px;
  line-height: 1.5;
  text-align: justify;
  hyphens: auto;
}

@media (max-width: 780px) {
  .stones { gap: 2rem; }
}
</style>
